import React, { FC } from 'react';
import { Alert, Col, Form, Row, Spin } from 'antd';
import { FormInstance } from 'antd/lib/form';
import {
  OptionProps,
  ReportKey,
  ReportsFilterOptionsResponse,
} from '@models/interfaces';
import { PrimaryButton, RangePicker, Select } from '@shared/modules';
import {
  isPaceTaskFilterSupported,
  isPermittingReport,
  SORT_ORDER_OPTIONS,
} from '../../models';

interface ReportDateFieldConfig {
  extra?: string;
  label: string;
  tooltip?: string;
}

const REPORT_DATE_FIELD_CONFIG: Partial<
  Record<ReportKey, ReportDateFieldConfig>
> = {
  [ReportKey.PermittingOverview]: {
    label: 'Actual Date Range',
    extra: 'Choose one Date Field; either boundary may be left open.',
  },
  [ReportKey.DatesAtAGlance]: {
    label: 'Milestone Date',
    tooltip:
      'Uses the actual date once a milestone is completed, otherwise the forecasted date.',
  },
  [ReportKey.OverdueTasks]: {
    label: 'Forecasted Date',
  },
  [ReportKey.NewClosedProjects]: {
    label: 'Report Period',
  },
  [ReportKey.ProjectDuration]: {
    label: 'Site Acq Complete Actual Date',
    tooltip:
      "Filters only the project's end date (Site Acq Complete actual) — the start date (first date the status became Active) isn't filterable.",
  },
};

interface ReportsFiltersProps {
  currentGroupByOptions: OptionProps[];
  currentSortByOptions: OptionProps[];
  filterOptions?: ReportsFilterOptionsResponse | null;
  filterOptionsError: boolean;
  filterOptionsLoading: boolean;
  form: FormInstance;
  onApplyFilters: () => Promise<void>;
  onResetFilters: () => void;
  projectTypeOptions: OptionProps[];
  selectedReport: ReportKey;
}

export const ReportsFilters: FC<ReportsFiltersProps> = ({
  currentGroupByOptions,
  currentSortByOptions,
  filterOptions,
  filterOptionsError,
  filterOptionsLoading,
  form,
  onApplyFilters,
  onResetFilters,
  projectTypeOptions,
  selectedReport,
}: ReportsFiltersProps) => {
  const showPaceTaskFilters = isPaceTaskFilterSupported(selectedReport);
  const dateFieldConfig = REPORT_DATE_FIELD_CONFIG[selectedReport];

  return (
    <section className="prov-reports-page__filters">
      {filterOptionsError && (
        <Alert
          className="prov-reports-page__alert"
          message="Unable to load filter options"
          type="error"
        />
      )}
      <Spin spinning={filterOptionsLoading}>
        <Form form={form} layout="vertical" autoComplete="off">
          <Row gutter={16}>
            {selectedReport === ReportKey.PermittingOverview && (
              <Col xl={6} md={8} sm={12} xs={24}>
                <Select
                  id="dateField"
                  label="Date Field"
                  options={filterOptions?.permittingDateFields ?? []}
                  elementProps={{ allowClear: true }}
                />
              </Col>
            )}
            {!!dateFieldConfig && (
              <Col xl={12} md={16} sm={24} xs={24}>
                <RangePicker
                  className="prov-reports-page__date-range"
                  id="dateRange"
                  label={dateFieldConfig.label}
                  required={false}
                  allowOpenRange
                  formItemProps={{
                    extra: dateFieldConfig.extra,
                    tooltip: dateFieldConfig.tooltip,
                  }}
                />
              </Col>
            )}
            <Col xl={6} md={8} sm={12} xs={24}>
              <Select
                id="projectType"
                label="Project Type"
                options={projectTypeOptions}
                elementProps={{
                  allowClear: true,
                  mode: 'multiple',
                  optionFilterProp: 'children',
                }}
              />
            </Col>
            <Col xl={6} md={8} sm={12} xs={24}>
              <Select
                id="projectSubtype"
                label="Project Subtype"
                options={filterOptions?.projectSubtypes ?? []}
                elementProps={{
                  allowClear: true,
                  mode: 'multiple',
                  optionFilterProp: 'children',
                }}
              />
            </Col>
            <Col xl={6} md={8} sm={12} xs={24}>
              <Select
                id="projectStatus"
                label="Project Status"
                options={filterOptions?.projectStatuses ?? []}
                elementProps={{
                  allowClear: true,
                  mode: 'multiple',
                  optionFilterProp: 'children',
                }}
              />
            </Col>
            <Col xl={6} md={8} sm={12} xs={24}>
              <Select
                id="progressStatus"
                label="Progress Status"
                options={filterOptions?.progressStatuses ?? []}
                elementProps={{
                  allowClear: true,
                  mode: 'multiple',
                  optionFilterProp: 'children',
                }}
              />
            </Col>
            <Col xl={6} md={8} sm={12} xs={24}>
              <Select
                id="state"
                label="State"
                options={filterOptions?.states ?? []}
                elementProps={{
                  allowClear: true,
                  mode: 'multiple',
                  optionFilterProp: 'children',
                }}
              />
            </Col>
            <Col xl={6} md={8} sm={12} xs={24}>
              <Select
                id="submarket"
                label="Submarket"
                options={filterOptions?.submarkets ?? []}
                elementProps={{
                  allowClear: true,
                  mode: 'multiple',
                  optionFilterProp: 'children',
                }}
              />
            </Col>
            <Col xl={6} md={8} sm={12} xs={24}>
              <Select
                id="towerOwner"
                label="Tower Owner"
                options={filterOptions?.towerOwners ?? []}
                elementProps={{
                  allowClear: true,
                  mode: 'multiple',
                  optionFilterProp: 'children',
                }}
              />
            </Col>
            <Col xl={6} md={8} sm={12} xs={24}>
              <Select
                id="providenceAgent"
                label="Providence Agent"
                options={filterOptions?.providenceAgents ?? []}
                elementProps={{
                  allowClear: true,
                  mode: 'multiple',
                  optionFilterProp: 'children',
                }}
              />
            </Col>
            {isPermittingReport(selectedReport) && (
              <Col xl={6} md={8} sm={12} xs={24}>
                <Select
                  id="permittingAgent"
                  label="Permitting Agent"
                  options={filterOptions?.permittingAgents ?? []}
                  elementProps={{
                    allowClear: true,
                    mode: 'multiple',
                    optionFilterProp: 'children',
                  }}
                />
              </Col>
            )}
            {showPaceTaskFilters && (
              <>
                <Col xl={6} md={8} sm={12} xs={24}>
                  <Select
                    id="projectNumber"
                    label="Project Number"
                    options={[]}
                    formItemProps={{
                      tooltip:
                        'Matches Project Bundle ID, Oracle Project #, PACE Project # or IWM Job #. Press Enter, comma or space after each number.',
                    }}
                    elementProps={{
                      allowClear: true,
                      mode: 'tags',
                      open: false,
                      placeholder: 'Bundle ID, Oracle, PACE or IWM #',
                      showArrow: false,
                      tokenSeparators: [',', ' '],
                    }}
                  />
                </Col>
                <Col xl={6} md={8} sm={12} xs={24}>
                  <Select
                    id="paceTaskScreen"
                    label="PACE Task Module"
                    options={filterOptions?.paceTaskScreens ?? []}
                    elementProps={{
                      allowClear: true,
                      mode: 'multiple',
                      optionFilterProp: 'children',
                    }}
                  />
                </Col>
                <Col xl={6} md={8} sm={12} xs={24}>
                  <Select
                    id="paceTask"
                    label="PACE Task"
                    options={filterOptions?.paceTasks ?? []}
                    elementProps={{
                      allowClear: true,
                      mode: 'multiple',
                      showSearch: true,
                      filterOption: (
                        input: string,
                        option?: { children?: unknown },
                      ) =>
                        !!option &&
                        String(option.children)
                          .toUpperCase()
                          .indexOf(String(input).toUpperCase()) !== -1,
                    }}
                  />
                </Col>
              </>
            )}
            {!!currentGroupByOptions.length && (
              <Col xl={6} md={8} sm={12} xs={24}>
                <Select
                  id="groupBy"
                  label="Group By"
                  options={currentGroupByOptions}
                  elementProps={{ allowClear: true }}
                />
              </Col>
            )}
            <Col xl={6} md={8} sm={12} xs={24}>
              <Select
                id="sortBy"
                label="Sort By"
                options={currentSortByOptions}
                elementProps={{ allowClear: true }}
              />
            </Col>
            <Col xl={6} md={8} sm={12} xs={24}>
              <Select
                id="sortOrder"
                label="Sort Order"
                options={SORT_ORDER_OPTIONS}
                elementProps={{ allowClear: true }}
              />
            </Col>
          </Row>
          <Row className="prov-reports-page__filter-actions">
            <PrimaryButton onClick={onApplyFilters} title="Apply" />
            <PrimaryButton
              onClick={onResetFilters}
              title="Reset"
              type="default"
            />
          </Row>
        </Form>
      </Spin>
    </section>
  );
};
